import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { NotificationsService } from 'angular2-notifications';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FormFieldsValidationService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-fields-validation.service';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { Field } from 'src/sb-tools/typescript/models/field.model';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { FieldsService } from '../../../core/services/ticket/fields.service';
import { TicketRightPanelService } from '../../../core/services/ticket-right-panel.service';
import { FieldValue } from '@form-field-module/common/models/field-value.model';
import { DetailedTicketDTO } from '../../../core/models/dto/detailed-ticket.dto';
import { LoaderComponent } from '../../../../sb-tools/typescript/components/loader/loader.component';
import { NgClass } from '@angular/common';
import { FormFieldComponent } from '../../../../sb-modules/Frontend-form-field-module/form-field/form-field.component';

@Component({
    selector: 'app-edit-fields',
    templateUrl: './edit-fields.component.html',
    styleUrls: ['./edit-fields.component.scss'],
    imports: [TranslateModule, LoaderComponent, FormsModule, NgClass, FormFieldComponent],
})
export class EditFieldsComponent implements OnInit, OnDestroy {
    @Input() ticket: DetailedTicketDTO;

    @ViewChild('ticketForm') ticketForm: NgForm;

    fields: Field[] = [];
    formTypesEnum = FormType;
    isLoaded: boolean;
    isSaving: boolean;

    private unsubscriber = new Subject<void>();

    constructor(
        private fieldsService: FieldsService,
        private formFieldsValidationService: FormFieldsValidationService,
        private formCommonDataService: FormCommonDataService,
        public formService: FormService,
        private notificationsService: NotificationsService,
        private ticketRightPanelService: TicketRightPanelService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.getFields(this.ticket.id);
    }

    closePanel(): void {
        this.ticketRightPanelService.close();
    }

    get hasEditableFields(): boolean {
        return this.fields.some(
            (field) =>
                field.isReadOnly !== true &&
                field.type !== FieldType.formattedText &&
                field.type !== FieldType.formHeader,
        );
    }

    save(): void {
        if (!this.hasEditableFields) {
            return;
        }

        this.formService.isSubmitted = true;

        const editableFields = this.fields.filter((field) => field.isReadOnly !== true);
        if (this.ticketForm.valid && this.formFieldsValidationService.isRequiredValuesFilled(editableFields)) {
            this.isSaving = true;
            this.updateFields(this.fields);
        } else {
            setTimeout(() => {
                this.formFieldsValidationService.scrollToInvalidField('#editFieldsForm', '#formContainer');
            }, 100);
        }
    }

    private getFields(ticketId): void {
        this.fieldsService
            .getAll(ticketId)
            .pipe(
                switchMap((fields) => this.addMissingReadOnlyFields(fields)),
                takeUntil(this.unsubscriber),
            )
            .subscribe((data: Field[]) => {
                data.forEach((element) => {
                    if (element.isRequired && element.options.isNotRequiredForStaffs) {
                        element.isRequired = false;
                    }

                    // display all dependant fields
                    element.isShowedBecauseOfRule = true;
                });
                this.fields = data;
                this.fieldsService.initialFieldsValues = data.map((f) => f.value);
                this.fieldsService.editedFields = this.fields;
                if (this.ticketRightPanelService.data.isFocusNeeded) {
                    this.formCommonDataService.setFocusOnFirstFocusableElement(this.fields);
                }
                this.isLoaded = true;
            });
    }

    private addMissingReadOnlyFields(fields: Field[]): Observable<Field[]> {
        const formId = fields.find((field) => field.formId > 0)?.formId;
        const isFormTicket = [
            DetailedTicketSource.formExternal,
            DetailedTicketSource.formInternal,
            DetailedTicketSource.formForward,
        ].includes(this.ticket.detailedSource);

        if (!formId && !(isFormTicket && this.ticket.sourceName)) {
            return of(fields);
        }

        const formRequest = formId ? this.formService.getById(formId) : this.formService.get(this.ticket.sourceName);
        return formRequest.pipe(
            map((form) => {
                const missingFields = form.fields
                    .filter(
                        (field) =>
                            field.isReadOnly === true &&
                            !field.isMapToTickedField &&
                            field.type !== FieldType.formattedText &&
                            field.type !== FieldType.formHeader &&
                            !fields.some((storedField) => storedField.id === field.id),
                    )
                    .map((field) => Object.assign(new Field(), field, { formId: form.id, value: '' }));

                return [...fields, ...missingFields].sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
            }),
            // A deleted or unavailable form must not prevent editing the ticket's stored fields.
            catchError(() => of(fields)),
        );
    }

    private updateFields(fields: Field[]): void {
        const processedFields: FieldValue[] = fields
            .filter((field) => field.isReadOnly !== true)
            .map((field) => ({ ticketId: this.ticket.id, value: field.value ?? '', id: field.id }));

        this.fieldsService
            .update(this.ticket.id, processedFields)
            .pipe(
                switchMap(() => this.fieldsService.getAll(this.ticket.id)),
                finalize(() => {
                    this.isSaving = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((storedFields) => {
                this.ticket.fields = JSON.parse(JSON.stringify(storedFields));
                this.fieldsService.currentFields = JSON.parse(JSON.stringify(storedFields));
                this.fields = fields.map((field) => {
                    const storedField = storedFields.find((stored) => stored.id === field.id);
                    return Object.assign(field, { value: storedField?.value ?? '' });
                });
                this.fieldsService.editedFields = this.fields;
                this.fieldsService.emitFieldsUpdated();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Fields were successfully updated.'),
                );
                this.fieldsService.initialFieldsValues = this.fields.map((f) => f.value);
            });
    }

    ngOnDestroy(): void {
        this.fieldsService.editedFields.length = 0;
        this.fieldsService.initialFieldsValues.length = 0;
        this.formService.isSubmitted = false;
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
